import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail as sendPasswordReset,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
} from "firebase/firestore";
import {
  getStorage,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";

const defaultAuthDomain = "outlaw-app-3712d.firebaseapp.com";

function getRuntimeAuthDomain() {
  if (typeof window === "undefined") return defaultAuthDomain;

  const hostname = window.location.hostname;
  const hostedDomains = new Set([
    "strengthpilot.de",
    "www.strengthpilot.de",
    "outlawcoaching.app",
    "www.outlawcoaching.app",
    "outlaw-app-3712d.web.app",
    "outlaw-app-3712d.firebaseapp.com",
  ]);

  // Keep Firebase Auth helper iframes first-party on hosted domains. This avoids
  // Safari treating the default firebaseapp.com iframe as third-party storage.
  return hostedDomains.has(hostname) ? hostname : defaultAuthDomain;
}

const firebaseConfig = {
  apiKey: "AIzaSyDfsEYDyCWo4TPtSjDutxC903-6uSU6o08",
  authDomain: getRuntimeAuthDomain(),
  projectId: "outlaw-app-3712d",
  storageBucket: "outlaw-app-3712d.firebasestorage.app",
  messagingSenderId: "867305483992",
  appId: "1:867305483992:web:403d434ae38c9e9852f1ad",
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, "us-central1");
export const functionsEurope = getFunctions(app, "europe-west1");
export const functionsFrankfurt = getFunctions(app, "europe-west3");

const PASSWORD_REQUIREMENTS_MESSAGE =
  "Your password is too weak. Please use at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character.";

export function getFriendlyAuthErrorMessage(error, fallback = "Authentication failed") {
  const errorCode = String(error?.code || "").toLowerCase();
  const errorMessage = String(error?.message || "");
  const normalizedMessage = errorMessage.toLowerCase();

  if (
    errorCode === "auth/weak-password" ||
    errorCode === "auth/password-does-not-meet-requirements" ||
    errorCode === "auth/invalid-password" ||
    normalizedMessage.includes("weak-password") ||
    normalizedMessage.includes("password-does-not-meet-requirements") ||
    normalizedMessage.includes("password policy") ||
    normalizedMessage.includes("password strength") ||
    (normalizedMessage.includes("password") && normalizedMessage.includes("require"))
  ) {
    return PASSWORD_REQUIREMENTS_MESSAGE;
  }

  if (errorCode === "auth/invalid-email" || normalizedMessage.includes("invalid-email")) {
    return "Please enter a valid email address.";
  }

  if (errorCode === "auth/email-already-in-use" || normalizedMessage.includes("email-already") || normalizedMessage.includes("already in use")) {
    return "This email address is already in use.";
  }

  return errorMessage || fallback;
}

export async function login(email, password) {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error("Login fehlgeschlagen:", error);
    throw error;
  }
}

export async function registerWithInvitationCode({ email, password, username, invitationCode }) {
  const normalizedEmail = String(email || "").trim();
  const normalizedUsername = String(username || "").trim();
  const code = String(invitationCode || "").trim().toUpperCase();
  const hasInvitationCode = code.length > 0;

  if (!normalizedEmail) throw new Error("Email is required");
  if (!password) throw new Error("Password is required");
  if (!normalizedUsername) throw new Error("Username is required");
  if (hasInvitationCode && code.length !== 6) throw new Error("Invitation code must be 6 characters long");

  let authResult;
  try {
    authResult = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
  } catch (err) {
    throw new Error(getFriendlyAuthErrorMessage(err, "Registration failed"));
  }
  const userId = authResult.user?.uid;
  if (!userId) throw new Error("Registration failed: missing userId");

  // Create the user profile document (required for AuthContext to accept the session).
  await setDoc(
    doc(db, "users", userId),
    {
      username: normalizedUsername,
      email: normalizedEmail,
      isCoach: false,
      isAdmin: false,
      registeredAt: Date.now(),
    },
    { merge: true }
  );

  // Registration without invitation code is allowed.
  if (!hasInvitationCode) {
    return { userId, coachId: null, coachName: null, isCoach: false };
  }

  // Redeem invitation code via the EU-hosted callable (warm, co-located with
  // Firestore EU). One retry on transient network errors is plenty — deterministic
  // failures (invalid/expired code) short-circuit immediately.
  const callable = httpsCallable(functionsEurope, "use_invitation_code");
  let data = null;
  let lastError = null;

  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const result = await callable({ code });
      data = result?.data;
      if (data && typeof data === "object" && data.success === false) {
        lastError = new Error(data.error || "Invitation code failed");
        break; // don't retry deterministic failures
      }
      lastError = null;
      break;
    } catch (err) {
      console.error(`[register] use_invitation_code attempt ${attempt}/2 failed:`, err?.message);
      lastError = err;
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 800));
      }
    }
  }

  // IMPORTANT: do NOT delete the auth account on code-redemption failure.
  // The user is validly registered; surface a clear error so they can retry
  // the code from the /subscribe or a recovery screen without losing their
  // account.
  if (lastError) {
    throw lastError;
  }

  const isCoach = Boolean(data?.isCoach) || data?.kind === "coach_role";
  const isCompetitionStaff = data?.kind === "competition_staff";
  const coachId = data?.coachId || null;

  // Athlete path requires a coachId. Role invitations are assigned server-side.
  if (!coachId && !isCompetitionStaff) {
    throw new Error("Coach assignment failed. Please check your invitation code and try again.");
  }

  // Mirror coachId on profile immediately (athlete path). For coach-role the
  // backend already set isCoach=true; no client mirror needed.
  if (!isCoach && !isCompetitionStaff) {
    await setDoc(
      doc(db, "users", userId),
      { coachId },
      { merge: true }
    );
  }

  return {
    userId,
    coachId,
    coachName: data?.coachName || null,
    isCoach,
    isCompetitionStaff,
    competitionEventIds: data?.eventIds || [],
  };
}

export async function generateCoachInvitationCode() {
  const callable = httpsCallable(functionsEurope, "generate_coach_invitation_code");
  const result = await callable({});
  const data = result?.data;
  if (data && typeof data === "object" && data.success === false) {
    throw new Error(data.error || "Failed to generate invitation code");
  }
  return data;
}

export async function sendCoachInvitationWithCode(email) {
  const normalizedEmail = String(email || "").trim();
  if (!normalizedEmail) throw new Error("Email is required");

  const callable = httpsCallable(functionsEurope, "send_coach_invitation_with_code");
  const result = await callable({ email: normalizedEmail });
  const data = result?.data;
  // "already_exists" is returned as structured data so the UI can offer recovery
  // (copy code, resend email, revoke). All other errors keep throwing.
  if (data && typeof data === "object" && data.success === false) {
    if (data.error === "already_exists") return data;
    throw new Error(data.message || data.error || "Failed to send invitation");
  }
  return data;
}

export async function listPendingCoachInvitations(kind = "all") {
  const callable = httpsCallable(functionsEurope, "list_pending_coach_invitations");
  const result = await callable({ kind });
  const data = result?.data;
  if (data && typeof data === "object" && data.success === false) {
    throw new Error(data.error || "Failed to load invitations");
  }
  return Array.isArray(data?.invitations) ? data.invitations : [];
}

export async function revokeCoachInvitation(code, eventId = null) {
  const callable = httpsCallable(functionsEurope, "revoke_coach_invitation");
  const result = await callable({ code, ...(eventId ? {eventId} : {}) });
  const data = result?.data;
  if (data && typeof data === "object" && data.success === false) {
    throw new Error(data.error || "Failed to revoke invitation");
  }
  return data;
}

export async function resendCoachInvitation(code) {
  const callable = httpsCallable(functionsEurope, "resend_coach_invitation");
  const result = await callable({ code });
  const data = result?.data;
  if (data && typeof data === "object" && data.success === false) {
    throw new Error(data.error || "Failed to resend invitation");
  }
  return data;
}

export async function sendAdminCoachInvitationWithCode(email) {
  const normalizedEmail = String(email || "").trim();
  if (!normalizedEmail) throw new Error("Email is required");

  const callable = httpsCallable(functionsEurope, "send_admin_coach_invitation_with_code");
  const result = await callable({ email: normalizedEmail });
  const data = result?.data;
  // Pass through structured "already_exists" so the UI can offer revoke+retry.
  if (data && typeof data === "object" && data.success === false && data.error !== "already_exists") {
    throw new Error(data.message || data.error || "Failed to send coach invitation");
  }
  return data;
}

export async function sendCompetitionRefereeInvitation({ email, eventIds }) {
  const normalizedEmail = String(email || "").trim();
  if (!normalizedEmail) throw new Error("Email is required");
  if (!Array.isArray(eventIds) || eventIds.length === 0) {
    throw new Error("Select at least one competition");
  }
  const callable = httpsCallable(functionsEurope, "send_competition_referee_invitation");
  const result = await callable({ email: normalizedEmail, eventIds });
  const data = result?.data;
  if (data && typeof data === "object" && data.success === false && data.error !== "already_exists") {
    throw new Error(data.message || data.error || "Failed to send referee invitation");
  }
  return data;
}

export async function listCompetitionReferees(eventIds) {
  const callable = httpsCallable(functionsEurope, "list_competition_referees");
  const result = await callable({ eventIds });
  const data = result?.data;
  if (data?.success === false) throw new Error(data.error || "Failed to load referees");
  return Array.isArray(data?.referees) ? data.referees : [];
}

export async function removeCompetitionReferee({ userId, eventIds }) {
  const callable = httpsCallable(functionsEurope, "remove_competition_referee");
  const result = await callable({ userId, eventIds });
  const data = result?.data;
  if (data?.success === false) throw new Error(data.error || "Failed to remove referee");
  return data;
}

export const sendPasswordResetEmail = async (email) => {
  return await sendPasswordReset(auth, email);
};
